import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const About: React.FC = () => {
  const { t } = useTranslation();
  const [flippedCards, setFlippedCards] = useState<string[]>([]);
  const [activeValue, setActiveValue] = useState(0);
  const [rotation, setRotation] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref to avoid stale closure in goToValue when carousel has ticked since last render
  const activeValueRef = useRef(0);

  const values = [
    {
      key: 'kindness',
      icon: (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      ),
    },
    {
      key: 'listening',
      icon: (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
    },
    {
      key: 'pragmatism',
      icon: (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
    },
    {
      key: 'excellence',
      icon: (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
    },
    {
      key: 'agility',
      icon: (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      ),
    },
  ];

  const toggleFlip = (key: string) => {
    setFlippedCards(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const startCarousel = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveValue(v => {
        const next = (v + 1) % 5;
        activeValueRef.current = next;
        return next;
      });
      setRotation(r => r - 72);
    }, 3400);
  };

  useEffect(() => {
    startCarousel();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToValue = (idx: number) => {
    const current = activeValueRef.current;
    if (idx === current) return;
    const steps = ((idx - current) + 5) % 5;
    activeValueRef.current = idx;
    setRotation(r => r - steps * 72);
    setActiveValue(idx);
    startCarousel();
  };

  const renderCard = (key: string, icon: React.ReactNode, height: number = 200) => (
    <div
      key={key}
      className="relative cursor-pointer"
      style={{ height }}
      onClick={() => toggleFlip(key)}
    >
      <div
        className={`absolute w-full h-full transition-all duration-500 preserve-3d ${
          flippedCards.includes(key) ? 'rotate-y-180' : ''
        }`}
      >
        <div className="absolute w-full h-full backface-hidden">
          <div
            className="w-full h-full flex flex-col items-center justify-center p-4"
            style={{ borderRadius: 'var(--radius-xl)', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
          >
            <div className="mb-3 flex items-center justify-center" style={{ color: 'var(--color-primary)' }}>
              {icon}
            </div>
            <h3 className="capitalize text-center" style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '0.9375rem', color: 'var(--color-primary)' }}>
              {t(`values.${key}`)}
            </h3>
          </div>
        </div>
        <div className="absolute w-full h-full backface-hidden rotate-y-180">
          <div
            className="w-full h-full flex items-center justify-center p-4"
            style={{ background: 'var(--color-primary-subtle)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 'var(--radius-xl)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
          >
            <p className="text-center" style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', lineHeight: 1.6, color: 'var(--color-text-secondary)' }}>
              {t(`values.descriptions.${key}`)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-16 space-y-24">
      {/* Introduction */}
      <section className="max-w-4xl mx-auto text-center">
        <h1
          className="mb-6"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: 'var(--color-text-primary)',
          }}
        >
          {t('about.title', 'About Colibri Technologies')}
        </h1>
        <div
          className="mx-auto mb-8"
          style={{ width: 60, height: 3, background: 'var(--gradient-primary)', borderRadius: 2 }}
        />
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1.125rem',
            lineHeight: 1.75,
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('about.description', 'We are a team of passionate technologists dedicated to creating innovative solutions that drive business success.')}
        </p>
      </section>

      {/* Values */}
      <section className="relative overflow-hidden rounded-2xl py-16 px-4" style={{ borderRadius: 'var(--radius-2xl)' }}>
        {/* Background glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 50% -20%, rgba(16,185,129,0.04) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'rgba(255,255,255,0.01)',
            border: '1px solid rgba(255,255,255,0.03)',
            borderRadius: 'var(--radius-2xl)',
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto">
          <h2
            className="text-center mb-12"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: '2rem',
              color: 'var(--color-text-primary)',
            }}
          >
            {t('about.values.title', 'Our Values')}
          </h2>

          {/* Desktop: 5 in a row */}
          <div className="hidden md:grid md:grid-cols-5 gap-6">
            {values.map(({ key, icon }) => renderCard(key, icon, 200))}
          </div>

          {/* Mobile: rotating pentagon */}
          <div className="md:hidden">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
            {/* Pentagon container */}
            <div style={{ position: 'relative', width: 240, height: 240 }}>
              {/* Rotating group */}
              <div
                style={{
                  position: 'absolute', inset: 0,
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 700ms cubic-bezier(0.65,0,0.35,1)',
                }}
              >
                {values.map(({ key, icon }, i) => {
                  const a = i * 72 * (Math.PI / 180);
                  const R = 86, cx = 120, cy = 120, sz = 52;
                  const lx = cx + R * Math.sin(a) - sz / 2;
                  const ly = cy - R * Math.cos(a) - sz / 2;
                  const active = i === activeValue;
                  return (
                    <div
                      key={key}
                      onClick={() => goToValue(i)}
                      style={{
                        position: 'absolute',
                        left: lx, top: ly,
                        width: sz, height: sz,
                        transform: `rotate(${-rotation}deg) scale(${active ? 1.45 : 0.8})`,
                        transition: 'transform 700ms cubic-bezier(0.65,0,0.35,1)',
                        transformOrigin: 'center',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '50%',
                        background: active ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${active ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.07)'}`,
                        color: active ? 'var(--color-primary)' : 'rgba(255,255,255,0.3)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        zIndex: active ? 2 : 1,
                        boxShadow: active ? '0 0 16px rgba(16,185,129,0.25)' : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {icon}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active label - fades between values */}
            <div style={{ position: 'relative', height: 130, width: '100%' }}>
              {values.map(({ key }, i) => (
                <div
                  key={key}
                  style={{
                    position: 'absolute', left: 0, right: 0, top: 0,
                    opacity: i === activeValue ? 1 : 0,
                    transform: `translateY(${i === activeValue ? 0 : 6}px)`,
                    transition: 'opacity 350ms ease, transform 350ms ease',
                    textAlign: 'center',
                    padding: '0 24px',
                    pointerEvents: i === activeValue ? 'auto' : 'none',
                  }}
                >
                  <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '1rem', color: 'var(--color-primary)', marginBottom: 6 }}>
                    {t(`values.${key}`)}
                  </h3>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', lineHeight: 1.6, color: 'var(--color-text-secondary)', margin: 0 }}>
                    {t(`values.descriptions.${key}`)}
                  </p>
                </div>
              ))}
            </div>

            {/* Dots */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 12 }}>
              {values.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToValue(idx)}
                  aria-label={`Valeur ${idx + 1}`}
                  style={{
                    width: idx === activeValue ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    background: idx === activeValue ? 'var(--color-primary)' : 'rgba(255,255,255,0.2)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'width 300ms ease, background 300ms ease',
                  }}
                />
              ))}
            </div>
          </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
